// src/components/products/raychem/RaychemFeatures.jsx
import React from "react";
import RichTextRenderer from "../../common/RichTextRenderer";
import ProductContainerImage from "../ProductContainerImage";
import {
  PRODUCT_DETAIL_BODY_TEXT,
  PRODUCT_DETAIL_CONTENT_WRAP,
} from "../../../utils/productDetailTypography";

const RaychemFeatures = ({ title, features, safeText, onMediaClick }) => {
  if (!title) return null;

  const renderContent = (featureContent) => {
    if (!featureContent) return null;

    if (Array.isArray(featureContent)) {
      return (
        <RichTextRenderer
          content={featureContent}
          className="max-w-none space-y-4 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_li]:mb-2"
          textColorClass="text-gray-700"
          linkColorClass="product-link text-lg lg:text-xl"
          bodyTextClass={PRODUCT_DETAIL_BODY_TEXT}
          compactHeadings
        />
      );
    }

    return (
      <div
        className={`${PRODUCT_DETAIL_BODY_TEXT} whitespace-pre-line text-gray-700`}
      >
        {safeText(featureContent)}
      </div>
    );
  };

  const renderMedia = (youtubeId, mediaItems, featureTitle) => {
    if (youtubeId) {
      return (
        <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={featureTitle || "YouTube video"}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    if (mediaItems && mediaItems.length > 0) {
      return (
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
          {mediaItems.map((mediaItem, mediaIndex) => {
            const mediaUrl = mediaItem?.url;
            if (!mediaUrl) return null;

            const altText = safeText(
              mediaItem?.alternativeText ||
                mediaItem?.alt ||
                mediaItem?.name ||
                featureTitle ||
                `Media ${mediaIndex + 1}`
            );

            return (
              <ProductContainerImage
                sizing="intrinsic"
                key={mediaItem.id || mediaIndex}
                src={mediaUrl}
                alt={altText}
                className="cursor-zoom-in transition-transform duration-200 hover:scale-[1.02]"
                onClick={() => {
                  if (!onMediaClick) return;
                  onMediaClick({
                    type: "photo",
                    title: altText,
                    imageUrl: mediaUrl,
                  });
                }}
              />
            );
          })}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="mb-12 md:mb-16">
      <div className="product-panel-elevated rounded-3xl overflow-hidden">
        <div className="product-panel-header">
          <h2>
            {safeText(title)}
          </h2>
        </div>

        <div className="p-6 sm:p-8 lg:p-10">
          {features && features.length > 0 ? (
            <div className="space-y-6 sm:space-y-8">
              {features.map((feature, index) => {
                const featureTitle = safeText(feature.title);
                const featureContent = feature.content;
                const youtubeId = feature.youtube_id;
                const mediaItems = feature.media || [];

                const hasMedia =
                  youtubeId || (mediaItems && mediaItems.length > 0);
                const hasContent = !!featureContent;

                const featureToneClass =
                  index % 2 === 0
                    ? "product-feature-card-even border"
                    : "product-feature-card-odd border";

                return (
                  <article
                    key={feature.id || index}
                    className={`rounded-2xl border shadow-sm p-5 sm:p-7 md:p-8 ${featureToneClass}`}
                  >
                    {featureTitle && (
                      <h3 className="text-lg sm:text-xl md:text-2xl leading-tight font-bold product-feature-title mb-5 pb-3 border-b">
                        {featureTitle}
                      </h3>
                    )}

                    {hasMedia && hasContent ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
                        <div className={`min-w-0 ${PRODUCT_DETAIL_CONTENT_WRAP}`}>
                          {renderContent(featureContent)}
                        </div>

                        <div className="flex items-center justify-center">
                          {renderMedia(youtubeId, mediaItems, featureTitle)}
                        </div>
                      </div>
                    ) : hasContent && !hasMedia ? (
                      <div className="flex justify-center">
                        <div
                          className={`w-full max-w-3xl text-center ${PRODUCT_DETAIL_CONTENT_WRAP}`}
                        >
                          {renderContent(featureContent)}
                        </div>
                      </div>
                    ) : !hasContent && hasMedia ? (
                      <div className="flex justify-center">
                        <div className="max-w-3xl w-full flex items-center justify-center">
                          {renderMedia(youtubeId, mediaItems, featureTitle)}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-teal-800/80">
              <p>No features available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaychemFeatures;
